import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useLessons, useBookLesson, useCancelLesson, reservationError } from "@/api/queries";
import { ClassCard, SegmentedControl } from "@/design-system";
import type { ClassCardStatus } from "@/design-system";
import { Button } from "@/design-system";
import { Icon } from "@/components/Icon";
import { useAuth } from "@/auth";
import {
  formatDay,
  formatDuration,
  formatLongDate,
  formatTime,
  formatWeekday,
} from "@/lib/format";
import {
  addLocalDays,
  getStudioTimezone,
  zonedDateKey,
  zonedParts,
  zonedWallTimeToUtc,
} from "@/lib/timezone";
import { isBookingsClosed, isBookingsNotOpen } from "@/lib/booking";
import { defaultLessonsWindow } from "@/lib/lessonsWindow";
import { instructorLabel } from "@/lib/instructor";
import { displayName } from "@/lib/user";
import { useOnline } from "@/lib/useOnline";
import { OfflineErrorState } from "@/components/OfflineErrorState";
import { RefreshButton } from "@/components/RefreshButton";
import { useIsDesktop } from "@/layout/useIsDesktop";
import { LessonDetailPane } from "./LessonDetailPane";
import styles from "./ScheduleScreen.module.css";
import type { Lesson } from "@/lib/types";

/**
 * Schedule — the student's home for `/calendar`.
 *
 * Data layer is `useLessons` (react-query) against
 * `GET /api/lessons?from=&to=`. We fetch the full 14-day default
 * window once and let the day-picker filter client-side, which keeps
 * day-switching instant. A user changing the range via the segmented
 * control re-runs the query (the 14-day `to` is the only knob in the
 * POC).
 *
 * Booking and cancel go through `useBookLesson` / `useCancelLesson`.
 * Both mutations update the react-query cache optimistically and roll
 * back on error, with `onSettled` always re-invalidating so the
 * server is the final source of truth. The 422 contract tags
 * (`full`, `already_booked`, `too_late`) are surfaced through the
 * `error.body` payload that `api/client.ts` already populates.
 *
 * Booking-cutoff (issue #36): a lesson whose `booking_cutoff_minutes`
 * has elapsed renders in the `closed` state — its card shows
 * "Bookings closed" and the Book action is disabled. A booked lesson
 * past the cutoff also disables the row's cancel action, since the
 * close window applies to both directions.
 */

type View = "list" | "calendar";

interface DayChip {
  key: string;
  date: Date;
}

/**
 * The next seven local calendar days in the studio timezone. Each chip
 * carries the instant of local noon, so `formatDay` / `formatWeekday`
 * render the right day regardless of the offset (issue #203).
 */
function buildDayChips(): DayChip[] {
  const timezone = getStudioTimezone();
  const today = zonedParts(new Date(), timezone);

  return Array.from({ length: 7 }, (_, i) => {
    const parts = addLocalDays(today, i);
    const date = zonedWallTimeToUtc({ ...parts, hour: 12 }, timezone);
    return { key: zonedDateKey(date, timezone), date };
  });
}

function lessonStatus(
  lesson: Lesson,
  now: Date,
): "open" | "full" | "booked" | "closed" | "notOpen" {
  if (lesson.reserved_by_me) return "booked";
  if (isBookingsNotOpen(lesson, now)) return "notOpen";
  if (isBookingsClosed(lesson, now)) return "closed";
  if (lesson.spots_left > 0) return "open";
  return "full";
}

function classCardStatus(lesson: Lesson, now: Date): ClassCardStatus {
  if (lesson.reserved_by_me) return "booked";
  if (isBookingsNotOpen(lesson, now)) return "notOpen";
  if (isBookingsClosed(lesson, now)) return "closed";
  if (lesson.spots_left <= 0) return "full";
  return "open";
}

export function ScheduleScreen() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const online = useOnline();

  const days = useMemo(buildDayChips, []);
  const [activeDay, setActiveDay] = useState<string>(days[0]?.key ?? "");
  const [view, setView] = useState<View>("list");
  const [toast, setToast] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const isDesktop = useIsDesktop();

  const now = useMemo(() => new Date(), []);
  // Shared with `ClassDetailScreen` so both read one cache entry (#271).
  const range = useMemo(() => defaultLessonsWindow(now), [now]);

  const lessonsQuery = useLessons(token, range);
  const bookMutation = useBookLesson(token);
  const cancelMutation = useCancelLesson(token);

  const lessons: Lesson[] = lessonsQuery.data ?? [];

  const lessonsForDay = useMemo(() => {
    if (!activeDay) return lessons;
    return lessons.filter((l) => zonedDateKey(new Date(l.starts_at)) === activeDay);
  }, [lessons, activeDay]);

  const selectedLesson = useMemo(
    () => lessons.find((l) => l.id === selectedId) ?? null,
    [lessons, selectedId],
  );

  // On desktop the detail pane is always populated: keep a sensible
  // selection as the day / list changes (first class of the day).
  useEffect(() => {
    if (!isDesktop) return;
    if (lessonsForDay.length === 0) {
      setSelectedId(null);
    } else if (!lessonsForDay.some((l) => l.id === selectedId)) {
      setSelectedId(lessonsForDay[0].id);
    }
  }, [isDesktop, lessonsForDay, selectedId]);

  // Opening a class: a side pane on desktop, a full-page route on mobile.
  function openLesson(lessonId: number) {
    if (isDesktop) {
      setSelectedId(lessonId);
    } else {
      navigate(`/lessons/${lessonId}`);
    }
  }

  // Show a toast on the *result* of the latest mutation. We key the
  // effect off the mutation's settled identity (status + variables),
  // not just `isSuccess`, to avoid firing the same toast twice when
  // the user double-taps.
  const pendingLessonId = useMemo(() => {
    if (bookMutation.isPending && typeof bookMutation.variables === "number") {
      return bookMutation.variables;
    }
    if (cancelMutation.isPending && typeof cancelMutation.variables === "number") {
      return cancelMutation.variables;
    }
    return null;
  }, [bookMutation.isPending, bookMutation.variables, cancelMutation.isPending, cancelMutation.variables]);

  function showToast(msg: string) {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2600);
  }

  useEffect(() => {
    if (bookMutation.isError) {
      const tag = reservationError(bookMutation.error);
      const lesson = lessons.find((l) => l.id === bookMutation.variables);
      if (tag === "full") {
        showToast(t("schedule.toastFull"));
      } else if (tag === "already_booked") {
        showToast(t("schedule.toastAlreadyBooked"));
      } else if (tag === "too_late") {
        showToast(t("schedule.toastTooLate"));
      } else if (tag === "too_early") {
        showToast(t("schedule.toastTooEarly"));
      } else {
        showToast(
          bookMutation.error?.message ??
            (lesson
              ? t("schedule.toastBookFailedNamed", { title: lesson.title })
              : t("schedule.toastBookFailed")),
        );
      }
    }
    if (cancelMutation.isError) {
      showToast(
        cancelMutation.error?.message ?? t("schedule.toastCancelFailed"),
      );
    }
    // We intentionally depend on the *identity* of the settled state
    // by reading `data` / `error` references (the mutation object
    // is stable across renders). The variables change on every new
    // call, so we re-derive the toast only when the user clicks
    // again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bookMutation.isError,
    bookMutation.error,
    bookMutation.variables,
    bookMutation.data,
    cancelMutation.isError,
    cancelMutation.error,
    cancelMutation.variables,
    cancelMutation.data,
  ]);

  function book(lesson: Lesson) {
    bookMutation.mutate(lesson.id, {
      onSuccess: () => {
        showToast(
          t("schedule.toastBooked", {
            title: lesson.title,
            time: formatTime(lesson.starts_at),
          }),
        );
      },
    });
  }

  function cancel(lesson: Lesson) {
    cancelMutation.mutate(lesson.id, {
      onSuccess: () => {
        showToast(
          lesson.lesson_type === "assigned"
            ? t("schedule.toastUnenrolled", { title: lesson.title })
            : t("schedule.toastCancelled", { title: lesson.title }),
        );
      },
    });
  }

  const greetingName = user
    ? displayName(user)
    : t("schedule.greetingFallbackName");
  const todayLabel = activeDay
    ? formatLongDate(days.find((d) => d.key === activeDay)?.date ?? new Date())
    : formatLongDate(new Date());

  return (
    <div style={{ paddingBottom: 96 }}>
      {/* header */}
      <div style={{ padding: "20px 20px 8px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div
              style={{
                font: "var(--fw-regular) var(--fs-xs)/1 var(--font-body)",
                color: "var(--text-faint)",
              }}
            >
              {todayLabel}
            </div>
            <div
              style={{
                font: "var(--fw-extra) var(--fs-2xl)/1.1 var(--font-display)",
                color: "var(--text-strong)",
                letterSpacing: "var(--ls-tight)",
                marginTop: 4,
                textTransform: "capitalize",
              }}
            >
              {t("schedule.greeting", { name: greetingName })}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <RefreshButton
              onClick={() => lessonsQuery.refetch()}
              refreshing={lessonsQuery.isFetching}
            />
            <Link
              to="/my-reservations"
              data-testid="my-reservations-link"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                height: 36,
                padding: "0 14px",
                borderRadius: "var(--radius-pill)",
                background: "var(--surface-card)",
                border: "1px solid var(--border-subtle)",
                boxShadow: "var(--shadow-xs)",
                color: "var(--text-strong)",
                font: "var(--fw-semibold) var(--fs-sm)/1 var(--font-body)",
                textDecoration: "none",
              }}
            >
              <Icon name="calendar" size={16} />
              {t("nav.myBookings")}
            </Link>
          </div>
        </div>
      </div>

      <div className={styles.body}>
        <div>
      {/* day picker */}
      <div
        role="tablist"
        aria-label={t("schedule.ariaDay")}
        data-testid="day-picker"
        style={{
          display: "flex",
          gap: 8,
          padding: "12px 20px",
          overflowX: "auto",
        }}
      >
        {days.map((d) => {
          const active = d.key === activeDay;
          return (
            <button
              key={d.key}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={`day-${d.key}`}
              onClick={() => setActiveDay(d.key)}
              style={{
                flex: "none",
                width: 48,
                height: 64,
                borderRadius: "var(--radius-lg)",
                border: "none",
                cursor: "pointer",
                background: active ? "var(--brand)" : "var(--surface-card)",
                boxShadow: active ? "var(--shadow-brand)" : "var(--shadow-xs)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 4,
                transition: "transform var(--dur-fast) var(--ease-out)",
              }}
            >
              <span
                style={{
                  font: "var(--fw-semibold) var(--fs-3xs)/1 var(--font-body)",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: active ? "rgba(255,255,255,0.85)" : "var(--text-faint)",
                }}
              >
                {formatWeekday(d.date)}
              </span>
              <span
                style={{
                  font: "var(--fw-bold) var(--fs-lg)/1 var(--font-mono)",
                  color: active ? "#fff" : "var(--text-strong)",
                }}
              >
                {formatDay(d.date)}
              </span>
            </button>
          );
        })}
      </div>

      {/* view toggle + count */}
      <div
        style={{
          padding: "8px 20px 4px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          data-testid="day-class-count"
          style={{
            font: "var(--fw-bold) var(--fs-md)/1 var(--font-display)",
            color: "var(--text-strong)",
          }}
        >
          {t("schedule.classCount", { count: lessonsForDay.length })}
        </span>
        <SegmentedControl
          size="sm"
          options={[
            { label: t("schedule.viewList"), value: "list" },
            { label: t("schedule.viewCalendar"), value: "calendar" },
          ]}
          value={view}
          onChange={(v) => setView(v as View)}
        />
      </div>

      {/* class list */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          padding: "12px 20px",
        }}
      >
        {lessonsQuery.isLoading && (
          <div
            data-testid="schedule-loading"
            style={{
              padding: "var(--space-6)",
              textAlign: "center",
              color: "var(--text-faint)",
            }}
          >
            {t("schedule.loading")}
          </div>
        )}

        {!lessonsQuery.isLoading && lessonsQuery.isError && (
          online ? (
            <div
              role="alert"
              data-testid="schedule-error"
              style={{
                background: "var(--status-danger-bg)",
                color: "var(--status-danger-fg)",
                padding: "var(--space-4)",
                borderRadius: "var(--radius-md)",
                font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
              }}
            >
              {lessonsQuery.error?.message ?? t("schedule.error")}
            </div>
          ) : (
            <OfflineErrorState />
          )
        )}

        {!lessonsQuery.isLoading &&
          !lessonsQuery.isError &&
          lessonsForDay.length === 0 && (
            <div
              data-testid="schedule-empty"
              style={{
                padding: "var(--space-8) var(--space-4)",
                textAlign: "center",
                color: "var(--text-muted)",
              }}
            >
              <div
                style={{
                  font: "var(--fw-bold) var(--fs-lg)/1.2 var(--font-display)",
                  color: "var(--text-strong)",
                  marginBottom: 8,
                }}
              >
                {t("schedule.emptyTitle")}
              </div>
              <div style={{ font: "var(--fw-regular) var(--fs-sm)/1.5 var(--font-body)" }}>
                {t("schedule.emptyBody")}
              </div>
            </div>
          )}

        {!lessonsQuery.isLoading &&
          !lessonsQuery.isError &&
          view === "list" &&
          lessonsForDay.map((c) => {
            const status = lessonStatus(c, now);
            const isPending = pendingLessonId === c.id;
            const cancelClosed = status === "booked" && isBookingsClosed(c, now);
            return (
              <div key={c.id} data-testid={`lesson-row-${c.id}`}>
                <ClassCard
                  data-testid={`lesson-card-${c.id}`}
                  time={formatTime(c.starts_at)}
                  duration={formatDuration(c.duration_minutes)}
                  title={c.title}
                  coach={instructorLabel(c)}
                  spotsLeft={c.spots_left}
                  status={classCardStatus(c, now)}
                  assigned={c.lesson_type === "assigned"}
                  disabled={!online}
                  ctaTitle={!online ? t("offline.bookingDisabled") : undefined}
                  onAction={(e) => {
                    e.stopPropagation();
                    if (isPending) return;
                    if (!online) return;
                    if (status === "open") book(c);
                  }}
                  onClick={() => openLesson(c.id)}
                  style={{ cursor: "pointer" }}
                  footer={
                    status === "booked" ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          gap: 4,
                          minWidth: 0,
                          maxWidth: "100%",
                        }}
                      >
                        <Button
                          variant="soft"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isPending) return;
                            cancel(c);
                          }}
                          disabled={isPending || cancelClosed || !online}
                          title={!online ? t("offline.bookingDisabled") : undefined}
                          data-testid={`lesson-card-${c.id}-cancel`}
                        >
                          {cancelClosed
                            ? t("schedule.cancelClosed")
                            : isPending
                              ? t("common.cancelling")
                              : c.lesson_type === "assigned"
                                ? t("schedule.unenroll")
                                : t("schedule.cancelReservation")}
                        </Button>
                        {cancelClosed && (
                          <span
                            data-testid={`lesson-card-${c.id}-cancel-closed`}
                            style={{
                              font: "var(--fw-regular) var(--fs-2xs)/1.3 var(--font-body)",
                              color: "var(--text-faint)",
                              textAlign: "right",
                            }}
                          >
                            {t("schedule.cancelClosedNote", {
                              duration: formatDuration(c.booking_cutoff_minutes),
                            })}
                          </span>
                        )}
                      </div>
                    ) : undefined
                  }
                />
              </div>
            );
          })}

        {!lessonsQuery.isLoading &&
          !lessonsQuery.isError &&
          view === "calendar" &&
          lessonsForDay.length > 0 && (
            <div className={styles.calendarGrid}>
              {lessonsForDay.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => openLesson(c.id)}
                  style={{
                    background: "var(--surface-card)",
                    border: "1px solid var(--border-subtle)",
                    borderRadius: "var(--radius-md)",
                    boxShadow: "var(--shadow-xs)",
                    padding: 14,
                    textAlign: "left",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  <span
                    style={{
                      font: "var(--fw-bold) var(--fs-md)/1 var(--font-mono)",
                      color: "var(--text-strong)",
                    }}
                  >
                    {formatTime(c.starts_at)}
                  </span>
                  <span
                    style={{
                      font: "var(--fw-semibold) var(--fs-sm)/1.2 var(--font-display)",
                      color: "var(--text-strong)",
                    }}
                  >
                    {c.title}
                  </span>
                  <span
                    style={{
                      font: "var(--fw-regular) var(--fs-2xs)/1 var(--font-body)",
                      color: "var(--text-faint)",
                    }}
                  >
                    {t("schedule.spotsLeftShort", { count: c.spots_left })}
                  </span>
                </button>
              ))}
            </div>
          )}
      </div>
        </div>

        {/* desktop master–detail pane (hidden on mobile via CSS) */}
        <aside className={styles.detailPane} aria-label={t("schedule.ariaClassDetails")}>
          <LessonDetailPane
            lesson={selectedLesson}
            pending={pendingLessonId === selectedLesson?.id}
            onBook={book}
            onCancel={cancel}
          />
        </aside>
      </div>

      {/* toast */}
      {toast && (
        <div
          role="status"
          data-testid="schedule-toast"
          style={{
            position: "fixed",
            left: 20,
            right: 20,
            bottom: 92,
            background: "var(--clay-900)",
            color: "#fff",
            borderRadius: "var(--radius-md)",
            padding: "14px 16px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            boxShadow: "var(--shadow-lg)",
            font: "var(--fw-medium) var(--fs-sm)/1.3 var(--font-body)",
            zIndex: 50,
          }}
        >
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: 999,
              background: "var(--sage-500)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "none",
              color: "#fff",
            }}
          >
            <Icon name="check" size={16} color="#fff" />
          </span>
          {toast}
        </div>
      )}
    </div>
  );
}
